import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { api, getTokens, onSessionLost, setTokens, type AuthTokens } from "@/lib/api";

export type UserRole = "ADMIN" | "ATENDENTE" | "REVENDEDOR" | "CLIENTE";

export interface Profile {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: "ATIVO" | "CONVITE_PENDENTE" | "DESATIVADO";
  whatsapp: string | null;
  teamName: string | null;
  createdAt: string;
  passwordUpdatedAt: string | null;
  /** Troca de e-mail esperando a confirmação pelo e-mail atual. */
  pendingEmail: string | null;
}

interface SessionValue {
  profile: Profile | null;
  /** Ainda verificando o token guardado — as telas esperam antes de redirecionar. */
  loading: boolean;
  isTeam: boolean;
  login: (email: string, password: string) => Promise<Profile>;
  register: (data: {
    name: string;
    email: string;
    password: string;
    whatsapp: string;
  }) => Promise<Profile>;
  logout: () => Promise<void>;
  /** Recarrega o perfil depois de uma edicao em "meus dados". */
  refreshProfile: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

/** Papeis que enxergam o painel. */
const TEAM_ROLES: UserRole[] = ["ADMIN", "ATENDENTE"];

/**
 * Sessao unica da aplicacao.
 *
 * O painel e a area do cliente compartilham o mesmo token porque sao o mesmo
 * usuario logado — o que muda e o PAPEL. Guardar duas sessoes paralelas so
 * criaria o estado esquisito de estar logado como duas pessoas ao mesmo tempo.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(() => getTokens() !== null);

  const loadProfile = useCallback(async () => {
    if (!getTokens()) {
      setProfile(null);
      return;
    }
    try {
      setProfile(await api.get<Profile>("/auth/me"));
    } catch {
      // Token invalido/expirado sem refresh possivel: volta a ser anonimo.
      setTokens(null);
      setProfile(null);
    }
  }, []);

  // Sessao guardada no storage: revalida contra a API na primeira carga.
  useEffect(() => {
    let active = true;
    void (async () => {
      await loadProfile();
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [loadProfile]);

  // O cliente HTTP avisa quando o refresh foi recusado de vez.
  useEffect(
    () =>
      onSessionLost(() => {
        setProfile(null);
        queryClient.clear();
      }),
    [queryClient],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const tokens = await api.post<AuthTokens>(
        "/auth/login",
        { email, password },
        { auth: false },
      );
      setTokens(tokens);
      const next = await api.get<Profile>("/auth/me");
      setProfile(next);
      // A sessao anterior pode ter deixado cache de outro usuario.
      queryClient.clear();
      return next;
    },
    [queryClient],
  );

  const register = useCallback(
    async (data: { name: string; email: string; password: string; whatsapp: string }) => {
      await api.post("/me/register", data, { auth: false });
      return login(data.email, data.password);
    },
    [login],
  );

  const logout = useCallback(async () => {
    const tokens = getTokens();
    if (tokens) {
      // Melhor esforco: mesmo se a API nao responder, a sessao local acaba.
      await api.post("/auth/logout", { refreshToken: tokens.refreshToken }).catch(() => {});
    }
    setTokens(null);
    setProfile(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<SessionValue>(
    () => ({
      profile,
      loading,
      isTeam: profile !== null && TEAM_ROLES.includes(profile.role),
      login,
      register,
      logout,
      refreshProfile: loadProfile,
    }),
    [profile, loading, login, register, logout, loadProfile],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession precisa estar dentro de <SessionProvider>");
  return value;
}
